import { defineStore } from "pinia";
import { ref, getDownloadURL } from "firebase/storage";
import { storage } from "@/firebase";
import { createPrefixedLogger } from "@/utils/logger";
import { useAreasStore } from "@/stores/areas";

const logger = createPrefixedLogger("MAP");

// Convention-based path mapping
// Files are stored at: areas/{areaId}/{path}
const RESOURCE_PATHS = {
  agebShape: "shapefiles/ageb.geojson",
  agebData: "data/ageb-census.json",
  elecSectionShape: "shapefiles/electoral-sections.geojson",
  elecDipData: "elections/diputados.json",
  elecAyunData: "elections/ayuntamientos.json",
  indexData: "data/indices.json",
};

// Map resource keys to menu map keys for availability updates
const RESOURCE_TO_MENU_MAP = {
  agebShape: "ageb",
  agebData: "ageb",
  elecSectionShape: "electoral",
  elecDipData: "electoral",
  elecAyunData: "electoral",
  indexData: "indices",
};

// IndexedDB configuration
const DB_NAME = "consulting-map-cache";
const DB_VERSION = 3; // Increment for convention-based paths
const STORE_NAME = "resources";
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

/**
 * Initialize IndexedDB for caching large map resources
 */
const initDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      logger.error("Failed to open IndexedDB:", request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: "key" });
        store.createIndex("timestamp", "timestamp", { unique: false });
        store.createIndex("area", "area", { unique: false });
      }
    };
  });
};

/**
 * Get cached resource from IndexedDB
 */
const getCachedResource = async (key, area) => {
  try {
    const db = await initDB();
    return new Promise((resolve) => {
      const transaction = db.transaction([STORE_NAME], "readonly");
      const store = transaction.objectStore(STORE_NAME);
      const cacheKey = `${area}:${key}`;
      const request = store.get(cacheKey);

      request.onsuccess = () => {
        const result = request.result;
        if (result && Date.now() - result.timestamp < CACHE_DURATION) {
          logger.debug("Cache hit for:", cacheKey);
          resolve(result.data);
        } else {
          if (result) {
            logger.debug("Cache expired for:", cacheKey);
          }
          resolve(null);
        }
      };

      request.onerror = () => {
        logger.warn("Cache read error:", request.error);
        resolve(null);
      };
    });
  } catch (error) {
    logger.warn("IndexedDB not available:", error);
    return null;
  }
};

/**
 * Store resource in IndexedDB cache
 */
const setCachedResource = async (key, area, data) => {
  try {
    const db = await initDB();
    return new Promise((resolve) => {
      const transaction = db.transaction([STORE_NAME], "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      const cacheKey = `${area}:${key}`;

      store.put({
        key: cacheKey,
        area,
        data,
        timestamp: Date.now()
      });

      transaction.oncomplete = () => {
        logger.debug("Cached resource:", cacheKey);
        resolve(true);
      };

      transaction.onerror = () => {
        logger.warn("Cache write error:", transaction.error);
        resolve(false);
      };
    });
  } catch (error) {
    logger.warn("Failed to cache resource:", error);
    return false;
  }
};

/**
 * Clear cache for a specific area or all cache
 */
const clearCache = async (area = null) => {
  try {
    const db = await initDB();
    return new Promise((resolve) => {
      const transaction = db.transaction([STORE_NAME], "readwrite");
      const store = transaction.objectStore(STORE_NAME);

      if (area) {
        // Clear only resources for specific area
        const index = store.index("area");
        const request = index.openCursor(IDBKeyRange.only(area));

        request.onsuccess = (event) => {
          const cursor = event.target.result;
          if (cursor) {
            store.delete(cursor.primaryKey);
            cursor.continue();
          }
        };
      } else {
        // Clear all cache
        store.clear();
      }

      transaction.oncomplete = () => {
        logger.debug("Cache cleared for:", area || "all");
        resolve(true);
      };
    });
  } catch (error) {
    logger.warn("Failed to clear cache:", error);
    return false;
  }
};

/**
 * Build the storage path for a resource based on area ID
 */
const buildStoragePath = (areaId, resourceKey) => {
  const subPath = RESOURCE_PATHS[resourceKey];
  if (!subPath) {
    logger.warn("Unknown resource key:", resourceKey);
    return null;
  }
  return `areas/${areaId}/${subPath}`;
};

/**
 * Get the download URL for a resource
 * Uses Firebase Storage SDK in production, proxy in development
 */
const getResourceUrl = async (areaId, resourceKey) => {
  const path = buildStoragePath(areaId, resourceKey);
  if (!path) return null;

  try {
    if (import.meta.env.DEV) {
      // In development, use the Vite proxy
      const port = import.meta.env.VITE_DEV_PORT || "5173";
      const bucket = import.meta.env.VITE_FIREBASE_STORAGE_BUCKET;
      return `http://localhost:${port}/v0/b/${bucket}/o/${encodeURIComponent(path)}?alt=media`;
    }

    // In production, use Firebase Storage SDK
    const storageRef = ref(storage, path);
    return await getDownloadURL(storageRef);
  } catch (error) {
    if (error.code === "storage/object-not-found") {
      logger.debug("Resource not found:", path);
      return null;
    }
    logger.error("Error getting download URL:", error);
    return null;
  }
};

export const useMapStore = defineStore("map", {
  state: () => ({
    area: "",
    zoom: 12,
    center: {
      lat: 19.4082954,
      lon: -99.1633882,
    },
    // Resource data (no URLs stored - paths derived from area ID)
    agebShapefile: null,
    agebData: null,
    sectionShape: null,
    deputyData: null,
    mayorData: null,
    indexData: null,
    // Loading states for individual resources
    loading: {
      agebShapefile: false,
      agebData: false,
      sectionShape: false,
      deputyData: false,
      mayorData: false,
      indexData: false,
    },
    // Error states
    errors: {},
  }),

  actions: {
    /**
     * Update map metadata (center, zoom) from area data
     * URLs are no longer stored - paths are convention-based
     */
    updateMapData(areaData) {
      if (areaData.center) {
        this.center = areaData.center;
      }
      if (areaData.zoom) {
        this.zoom = areaData.zoom;
      }
    },

    setArea(area) {
      this.area = area;
    },

    clearMapData() {
      this.area = "";
      this.zoom = 12;
      this.center = { lat: 19.4082954, lon: -99.1633882 };
      this.agebShapefile = null;
      this.agebData = null;
      this.sectionShape = null;
      this.deputyData = null;
      this.mayorData = null;
      this.indexData = null;
      this.errors = {};
    },

    /**
     * Update menu availability when a resource is not found
     */
    _updateMenuAvailability(resourceKey, available) {
      const menuKey = RESOURCE_TO_MENU_MAP[resourceKey];
      if (menuKey && this.area) {
        const areasStore = useAreasStore();
        areasStore.updateMapAvailability(this.area, menuKey, available);
      }
    },

    /**
     * Fetch a single resource with caching support
     * Uses convention-based paths: areas/{areaId}/{resourcePath}
     */
    async fetchResource(resourceKey, stateKey) {
      if (!this.area) {
        logger.warn("No area set, cannot fetch resource:", resourceKey);
        return null;
      }

      // Check if already loaded
      if (this[stateKey]) {
        logger.debug("Resource already in memory:", resourceKey);
        return this[stateKey];
      }

      this.loading[stateKey] = true;
      this.errors[stateKey] = null;

      try {
        // Try cache first
        const cached = await getCachedResource(resourceKey, this.area);
        if (cached) {
          this[stateKey] = cached;
          this.loading[stateKey] = false;
          return cached;
        }

        // Get download URL using convention-based path
        const url = await getResourceUrl(this.area, resourceKey);
        if (!url) {
          logger.debug("Resource not available:", resourceKey);
          this._updateMenuAvailability(resourceKey, false);
          this.loading[stateKey] = false;
          return null;
        }

        // Fetch from network
        logger.debug("Fetching from network:", resourceKey);
        const response = await fetch(url);

        if (!response.ok) {
          if (response.status === 404) {
            // 404 is expected for areas without certain data files
            logger.debug("Resource not found:", resourceKey);
            this._updateMenuAvailability(resourceKey, false);
            return null;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        // Store in state
        this[stateKey] = data;

        // Cache for future use
        await setCachedResource(resourceKey, this.area, data);

        logger.debug("Successfully fetched:", resourceKey);
        return data;
      } catch (error) {
        const errorMsg = `Failed to fetch: ${resourceKey}`;
        logger.error(errorMsg, error);
        this.errors[stateKey] = errorMsg;
        return null;
      } finally {
        this.loading[stateKey] = false;
      }
    },

    /**
     * Fetch multiple resources in parallel
     */
    async fetchResourcesParallel(resources) {
      const promises = resources.map(({ resourceKey, stateKey }) =>
        this.fetchResource(resourceKey, stateKey)
      );

      const results = await Promise.allSettled(promises);

      const success = results.filter(r => r.status === "fulfilled" && r.value !== null).length;
      const failed = results.filter(r => r.status === "rejected" || r.value === null).length;

      logger.debug(`Parallel fetch complete: ${success} succeeded, ${failed} failed`);

      return {
        success,
        failed,
        results: results.map((r, i) => ({
          resource: resources[i].resourceKey,
          status: r.status,
          data: r.status === "fulfilled" ? r.value : null,
          error: r.status === "rejected" ? r.reason : null,
        })),
      };
    },

    /**
     * Load AGEB data (shapefile + data) in parallel
     */
    async loadAGEBData() {
      return this.fetchResourcesParallel([
        { resourceKey: "agebShape", stateKey: "agebShapefile" },
        { resourceKey: "agebData", stateKey: "agebData" },
      ]);
    },

    /**
     * Load electoral data (shapes + deputy + mayor) in parallel
     */
    async loadElectoralData() {
      return this.fetchResourcesParallel([
        { resourceKey: "elecSectionShape", stateKey: "sectionShape" },
        { resourceKey: "elecDipData", stateKey: "deputyData" },
        { resourceKey: "elecAyunData", stateKey: "mayorData" },
      ]);
    },

    /**
     * Load index data (shapefile + index data) in parallel
     */
    async loadIndexData() {
      return this.fetchResourcesParallel([
        { resourceKey: "agebShape", stateKey: "agebShapefile" },
        { resourceKey: "indexData", stateKey: "indexData" },
      ]);
    },

    /**
     * Load all map data in parallel
     */
    async loadAllData() {
      return this.fetchResourcesParallel([
        { resourceKey: "agebShape", stateKey: "agebShapefile" },
        { resourceKey: "agebData", stateKey: "agebData" },
        { resourceKey: "elecSectionShape", stateKey: "sectionShape" },
        { resourceKey: "elecDipData", stateKey: "deputyData" },
        { resourceKey: "elecAyunData", stateKey: "mayorData" },
        { resourceKey: "indexData", stateKey: "indexData" },
      ]);
    },

    /**
     * Clear cache for current area or all areas
     */
    async clearCache(allAreas = false) {
      return clearCache(allAreas ? null : this.area);
    },

    /**
     * Force refresh a resource (bypass cache)
     */
    async refreshResource(resourceKey, stateKey) {
      // Clear from cache first
      await clearCache(this.area);
      // Clear from memory
      this[stateKey] = null;
      // Fetch fresh
      return this.fetchResource(resourceKey, stateKey);
    },
  },

  getters: {
    hasAgebData: (state) => state.agebData !== null && Object.keys(state.agebData).length > 0,
    hasAgebShapefile: (state) => state.agebShapefile !== null,
    hasSectionData: (state) => state.sectionShape !== null && Object.keys(state.sectionShape).length > 0,
    hasElectoralData: (state) => state.deputyData !== null && state.mayorData !== null,
    hasIndexData: (state) => state.indexData !== null,
    mapCenter: (state) => [state.center.lat, state.center.lon],
    isLoading: (state) => Object.values(state.loading).some(Boolean),
    loadingResources: (state) => Object.entries(state.loading)
      .filter(([, loading]) => loading)
      .map(([key]) => key),
  },
});
